import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useIntl } from 'react-intl';

import { StatusBannerAutoHide } from '@xing-com/banner';
import { Button } from '@xing-com/button';
import { IllustrationSpotImage } from '@xing-com/illustrations';

import { ZoomRangeInput } from './components/zoom-range-input/zoom-range-input';
import {
  MAX_IMAGE_SIZE,
  ACCEPTED_FILES,
  IMAGE_CLEANUP_DELAY,
} from './constants';
import { getCroppedImg, isValidFileSize } from './helpers/image-utils';
import { Styled } from './image-cropper-modal.styles';

const MIN_ZOOM = 1;
const MAX_ZOOM = 2;
const STEP = 0.05;

type ImageCropperModalProps = {
  show: boolean;
  onSubmit: (image: File | null, caption: string) => void;
  onClose: () => void;
  showCaptionInput?: boolean;
};

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  show,
  onSubmit,
  onClose,
  showCaptionInput = true,
}) => {
  const { formatMessage } = useIntl();
  const [error, setError] = useState<string | undefined>();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageBlobURL, setImageBlobURL] = useState<string>('');
  const [caption, setCaption] = useState<string>('');

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );
  const [zoom, setZoom] = useState<number>(1);

  const handleImageSubmit = useCallback(async () => {
    if (imageFile && croppedAreaPixels) {
      const croppedImageFile: File | null = await getCroppedImg(
        imageFile,
        croppedAreaPixels
      );
      onSubmit(croppedImageFile, caption);
    }
  }, [imageFile, caption, croppedAreaPixels, onSubmit]);

  const handleCancel = (): void => {
    onClose();
  };

  const onDrop = useCallback((acceptedFiles: Array<File>) => {
    acceptedFiles.forEach(async (file) => {
      if (!isValidFileSize(file, MAX_IMAGE_SIZE)) {
        setError(`File size exceeds the maximum limit of ${MAX_IMAGE_SIZE}MB.`);
        return;
      }

      const imageUrl = URL.createObjectURL(file);

      setImageFile(file);
      setImageBlobURL(imageUrl);
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: ACCEPTED_FILES.split(',').reduce(
      (acc, curr) => ({ ...acc, [curr]: [] }),
      {}
    ),
    maxSize: MAX_IMAGE_SIZE * 1024 * 1024,
  });

  useEffect(() => {
    if (!show && !!imageBlobURL) {
      setTimeout(() => {
        setImageBlobURL('');
        setZoom(1);
        URL.revokeObjectURL(imageBlobURL);
      }, IMAGE_CLEANUP_DELAY);
    }
  }, [show, imageBlobURL]);

  return (
    <Styled.ImageCropperPopUpWindow
      isShown={show}
      hide={() => handleCancel()}
      onDimmerClick={() => handleCancel()}
    >
      <Styled.ImageCropperContainer $hasImage={!!imageBlobURL}>
        {imageBlobURL ? (
          <React.Fragment>
            <Styled.ImageCropperImageContainer>
              <Cropper
                zoom={zoom}
                crop={crop}
                aspect={16 / 9}
                showGrid={false}
                image={imageBlobURL}
                zoomWithScroll={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Styled.ImageCropperImageContainer>

            <ZoomRangeInput
              value={zoom}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={STEP}
              onChange={(zoom) => {
                setZoom(zoom);
              }}
            />

            {showCaptionInput ? (
              <Styled.ImageCropperCaptionInput
                placeholder={formatMessage({
                  id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_CAPTION_PLACEHOLDER',
                })}
                value={caption}
                // @ts-expect-error FIXME: SC6
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCaption(e.target.value)
                }
              />
            ) : null}

            <Styled.ImageCropperButtonsContainer>
              <Button variant="tertiary" onClick={handleCancel}>
                {formatMessage({
                  id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_CANCEL_BUTTON',
                })}
              </Button>
              <Button onClick={handleImageSubmit}>
                {formatMessage({
                  id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_SUBMIT_BUTTON',
                })}
              </Button>
            </Styled.ImageCropperButtonsContainer>
          </React.Fragment>
        ) : (
          <Styled.ImageCropperDropZone {...getRootProps()}>
            <input
              {...getInputProps()}
              accept={ACCEPTED_FILES}
              data-qa="cover-image-file-picker"
            />

            <IllustrationSpotImage size="large" />

            <Styled.ImageCropperDropZoneHeadline size="xlarge">
              {formatMessage({
                id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_HEADLINE',
              })}
            </Styled.ImageCropperDropZoneHeadline>

            <Styled.ImageCropperDropZoneCopy size="small">
              {formatMessage({
                id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_FORMATS_DESCRIPTION',
              })}
            </Styled.ImageCropperDropZoneCopy>

            <Button variant="primary">
              {formatMessage({
                id: 'ARTICLES_ARTICLE_EDITOR_IMAGE_CROPPER_UPLOAD_BUTTON',
              })}
            </Button>
          </Styled.ImageCropperDropZone>
        )}
      </Styled.ImageCropperContainer>
      <StatusBannerAutoHide
        handleOnClose={() => setError(undefined)}
        display="fixed"
        show={!!error}
        variant="error"
      >
        {error}
      </StatusBannerAutoHide>
    </Styled.ImageCropperPopUpWindow>
  );
};
